<template>
  <router-link
    :to="{ name: isFromAccount ? 'accountPropertyDetail' : 'propertyDetail', params: { uuid: property.uuid } }"
    :id="`property_item_uuid_${property.uuid}`">
    <div class="property-card light new-layout shadow">
      <div class="card-attributes">
        <carousel v-bind="horizontalSettings" ref="horizontal" class="carousel-list" :wrap-around="true" :autoplay="0">
          <slide v-for="(item, index) in property.images" :key="index">
            <div :key="item.id" class="image-container">
              <img class="image" :class="{ darken: isSoldOut || isComingSoon }" :src="getImageUrl(item)" alt="" />
            </div>
          </slide>
          <template #addons>
            <pagination v-if="property.images.length > 1" />
              <template v-if="property.images.length > 1">
                <button class="carousel-btn carousel-btn--prev" @click.prevent="$refs.horizontal.prev()">
                  <img src="@/assets/img/icons/chevron-left.svg" alt="prev" />
                </button>
                <button class="carousel-btn carousel-btn--next" @click.prevent="$refs.horizontal.next()">
                  <img src="@/assets/img/icons/chevron-right.svg" alt="next" />
                </button>
              </template>
          </template>
        </carousel>
        <div class="card-info p-0 pb-3">
          <div v-if="isNew || isPromo || isPresale"
            class="mark-new-property d-flex justify-content-center align-items-center font-12">
            <img v-if="isPromo" width="14" class="mr-1" src="@/assets/img/icons/promo-filled.svg" alt="">
            <img v-else-if="isPresale" width="14" class="mr-1" src="@/assets/img/icons/presale.svg" alt="">
            <img v-else-if="isNew" width="14" class="mr-1" src="@/assets/img/icons/new.svg" alt="">
            <p v-if="isPromo" class="font-12 ml-1 text-white">{{ $t("PROPERTY_STATUS.PROMO") }} {{ promoTime }}</p>
            <p v-else-if="isPresale" class="font-12 ml-1 text-white">{{ $t("PROPERTY_STATUS.PRESALE") }} {{ presaleTime
              }}</p>
            <p v-else-if="isNew" class="font-12 ml-1 text-white">{{ $t("SWAP_TOKEN.NEW_PROPERTY") }}</p>
          </div>
          <div class="d-flex flex-row mt-3 align-items-start">
            <div class="cls-property-name d-flex flex-column align-items-start flex-grow-1">
              <component :is="nameTag" class="font-17 card-name m-0 text-white">{{ property.short_name || property.name }}</component>
              <span class="property-address font-12 d-flex align-items-center justify-content-end color-description">
                <img width="8" class="img-icon mr-1" src="@/assets/img/icons/location-white.svg" alt=""> <span>{{
                  location
                  }}</span>
              </span>
            </div>
            <div v-if="displayStatus" class="card-status">
              <p class="font-12 font-weight-bold">{{ displayStatus }}</p>
            </div>
            <div v-else class="cls-progress d-flex flex-column align-items-end">
              <b-progress class="bg-color-progress w-100" :max="100" :min="0" :precision="2" height="4px">
                <b-progress-bar class="bg-color-in-progress" :value="tokensLeftInfo.percent" />
              </b-progress>
              <span class="font-weight-bold font-12 color-stock">
                {{ formatNumberIntl(tokensLeftInfo.amount) }}/{{ formatNumberIntl(totalTokens) }}
              </span>
            </div>
          </div>
        </div>
        <div v-if="isPropertyOwned" class="cls-owned-label d-flex align-items-center justify-content-center">
          <img width="12" class="mr-1" src="@/assets/img/icons/owned.svg" alt="">
          <p class="font-12 ml-1 text-white text-owned">{{ $t("propertyDetail.OWNED") }}</p>
        </div>
        <div class="cls-property-meta d-flex align-items-center justify-content-start">
          <div class="meta-item">
            <img class="img-icon" src="@/assets/img/icons/property-bed.svg" alt="">
            <p class="meta-text p-0 m-0 font-12" :class="nameClass">
              {{ property.num_bed }}
            </p>
          </div>
          <div class="meta-item">
            <img class="img-icon" src="@/assets/img/icons/property-bath.svg" alt="">
            <p class="meta-text p-0 m-0 font-12" :class="nameClass">
              {{ property.num_bath }}
            </p>
          </div>
          <div class="meta-item">
            <img class="img-icon" src="@/assets/img/icons/property-sqm.svg" alt="">
            <p class="meta-text p-0 m-0 font-12" :class="nameClass">
              {{ property.sqm }} {{ sqmUnit }}
            </p>
          </div>
        </div>
      </div>
      <div class="d-flex flex-row align-items-end p-3">
        <div class="cls-card-separator d-flex flex-row justify-content-start flex-grow-1">
          <div class="d-flex flex-column" :id="'tooltip-target-ery-card' + property.id">
            <div class="d-flex flex-row align-items-center">
              <p class="font-14 font-weight-bold" :class="nameClass">{{ $t("propertyDetail.ERY") }}</p>
              <img class="img-info" src="@/assets/img/info-outlined.svg" alt="">
            </div>
            <p class="font-24 font-weight-bold" :class="nameClass" style="margin-top: -7px;">{{ property.ery ?
              `${property.ery}%` :
              'N/A' }}</p>
          </div>
          <b-tooltip variant="secondary" :target="'tooltip-target-ery-card' + property.id" triggers="hover"
            placement="top">
            {{ $t("propertyDetail.ERY_TOOLTIP") }}
          </b-tooltip>
          <div class="d-flex flex-column" :id="'tooltip-target-ary-card' + property.id" style="margin-left: 1.3rem;">
            <div class="d-flex flex-row align-items-center">
              <p class="font-14 font-weight-bold" :class="nameClass">{{ $t("propertyDetail.ARY") }}</p>
              <img class="img-info" src="@/assets/img/info-outlined.svg" alt="">
            </div>
            <p class="font-24 font-weight-bold" :class="nameClass" style="margin-top: -7px;">{{ property.ary ?
              `${property.ary}%` :
              'N/A' }}</p>
          </div>
          <b-tooltip variant="secondary" :target="'tooltip-target-ary-card' + property.id" triggers="hover"
            placement="top">
            <span v-html="$t('propertyDetail.ARY_TOOLTIP_HTML')"></span>
          </b-tooltip>
        </div>
        <div :class="{ disabled: isSoldOut || isComingSoon }" class="invest-now d-flex justify-content-center align-items-center">
          <p class="font-18 font-weight-bold text">{{ $t("INVEST_NOW") }}</p>
        </div>
      </div>
    </div>
  </router-link>
</template>

<script>
import { formattedDuration, urlImage, getPropertyTokenLeftsPercentage, formatNumberIntl } from "@/helpers/common"
import "vue3-carousel/dist/carousel.css"
import { Carousel, Navigation, Pagination, Slide } from "vue3-carousel"
import moment from "moment"

export default {
  components: {
    Carousel,
    Slide,
    Pagination,
    Navigation,
  },
  props: {
    property: {},
    isFromAccount: {
      type: Boolean,
      default: false,
    },
    // Tag used for the property name. Defaults to a paragraph; pages that list
    // these cards under a section heading pass "h3" so the card title takes its
    // place in the document outline. Opt-in, so pages whose heading structure
    // has not been reviewed keep rendering a <p>.
    nameTag: {
      type: String,
      default: "p",
    },
  },
  data() {
    return {
      promoTime: '',
      presaleTime: '',
      horizontalSettings: {
        itemsToShow: 1,
        snapAlign: "center",
      },
    };
  },
  mounted() {
    if ((this.isPresale || this.isPromo) && (this.showPresale || this.showPromo)) {
      const self = this;
      setInterval(() => {
        self.updateTimer();
      }, 1000);
    }
  },
  methods: {
    formatNumberIntl,
    getAvatar(images) {
      if (images && images.length) {
        return urlImage(images[0])
      }
      return ''
    },
    updateTimer() {
      if (this.isPresale) {
        const presaleEndsAt = this.property.presale_ends_at;
        const currentDate = new Date();
        const presaleDate = new Date(presaleEndsAt);
        if (currentDate >= presaleDate) {
          this.presaleTime = this.$t('propertyDetail.ENDED');
        } else {
          const diffTime = presaleDate.getTime() - currentDate.getTime();
          this.presaleTime = this.$t('propertyDetail.END_IN', { value: formattedDuration(diffTime) });
        }
      } else if (this.isPromo) {
        const promoEndsAt = this.property.promo_ends_at;
        const currentDate = new Date();
        const promoDate = new Date(promoEndsAt);
        if (currentDate >= promoDate) {
          this.promoTime = this.$t('propertyDetail.ENDED');
        } else {
          const diffTime = promoDate.getTime() - currentDate.getTime();
          this.promoTime = this.$t('propertyDetail.END_IN', { value: formattedDuration(diffTime) });
        }
      }
    },

    getImageUrl(image) {
      return urlImage(image)
    },
  },
  computed: {
    tokensLeftInfo() {
      const total = Number(this.property?.total_tokens) || 0;
      const sold = Number(this.property?.display_sold_tokens) || 0;
      if (total <= 0) return { percent: 0, displayPercentage: 0 };
      const left = Math.max(total - sold, 0);
      const [percent, displayPercentage] = getPropertyTokenLeftsPercentage(total, left);
      return { amount: left, percent: (percent | 0), displayPercentage: (displayPercentage | 0) };
    },
    isSoldOut() {
      return this.property.status === "sold" || this.property.display_sold_tokens === this.property.total_tokens
    },
    isPromo() {
      return this.property?.status === 'promo'
    },
    isPresale() {
      return this.property?.status === 'presale'
    },
    isNew() {
      return this.property?.is_new || false
    },
    isRuninngOutSoon() {
      return this.property?.is_running_out_soon || false
    },
    displayStatus() {
      if (this.isSoldOut) {
        return this.$t("PROPERTY_STATUS.SOLD_OUT");
      } else if (this.isRuninngOutSoon) {
        return this.$t("PROPERTY_STATUS.RUNNING_OUT_SOON");
      } else if (this.isComingSoon) {
        return this.$t("PROPERTY_STATUS.COMING_SOON");
      }
      return null
    },
    totalTokens() {
      return this.property.total_tokens
    },
    isComingSoon() {
      return this.property.status === 'coming_soon'
    },
    showPresale() {
      const presaleEndsAt = this.property.presale_ends_at;
      const currentDate = moment(new Date()).add(1, 'days');
      const presaleDate = new Date(presaleEndsAt);
      return currentDate > presaleDate;
    },
    showPromo() {
      const promoEndsAt = this.property.promo_ends_at;
      const currentDate = moment(new Date()).add(1, 'days');
      const promoDate = new Date(promoEndsAt);
      return currentDate > promoDate;
    },
    location() {
      if (this.property) {
        if (this.property.location) {
          return this.property.location.name
        } else if (this.property.metadata) {
          return this.property.metadata.address
        }
      }
      return "Indonesia"
    },
    getARY() {
      if (this.property.ary) {
        return `${this.property.ary}%`
      }
      return "N/A"
    },
    getLRY() {
      if (this.property.lry) {
        return `${this.property.lry}%`
      }
      return "N/A"
    },
    isPropertyOwned() {
      return !!this.property.is_owned
    },

    nameClass() {
      return {
        'color-stock': !this.isSoldOut,
        'color-sold-out': this.isSoldOut,
      };
    },

    isIndonesian() {
      return this.$i18n.locale.toLowerCase() === 'id';
    },

    sqmUnit() {
      if (this.isIndonesian) {
        return 'm²'
      }
      return 'sqm'
    },
  }
};
</script>
<style lang="scss" scoped>
// The property name renders as a <p> or an <h3> depending on the nameTag prop.
// Pin the typography so both tags produce identical output — otherwise the h3
// picks up Bootstrap's heading font-weight and line-height.
.card-name {
  font-family: inherit;
  font-weight: inherit;
  line-height: inherit;
}

.text-owned {
  @supports (-webkit-touch-callout: none) {
    padding-top: 0.2rem !important;   
}
}
</style>
